/**
 * v2.44 — Payment application panel.
 *
 * Renders a form to apply a payment against open invoices for one account.
 * After a successful POST /api/payments/apply it enters a "pending" state
 * showing the created ticket with Finalizar (timbrar REP) and Deshacer buttons.
 * Once finalized the confnum folio is shown and Deshacer is hidden.
 *
 * Props:
 *   sid  — store id used for cash-codes and for finalize/void calls
 *   anum — account to apply against
 *   user — optional; the caller's already-fetched User (AR.tsx has one in
 *     scope). Used only to AND licenseAllowsWrites into canSubmit — omitted
 *     (undefined) is treated as "license allows" (fail open), same posture
 *     as an absent user.license, so existing callers/tests that don't pass
 *     it are unaffected.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge, Button, Card, Select, SelectItem, Table, TableBody, TableCell,
  TableHead, TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import { fetchAccountStatement } from '../api/statement';
import { applyPayment, fetchCashCodes, finalizePayment, voidPayment } from '../api/payments';
import { localYMD } from '../lib/format';
import { t } from '../lib/i18n';
import { licenseAllowsWrites } from '../lib/roles';
import type { ApplyPaymentResult, CashCode, StatementLine, User } from '../api/types';

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });

// We only need a wide window (5y) to pull all open items.
function dateNDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localYMD(d);
}

interface RowEntry {
  line: StatementLine;
  applyAmt: string; // controlled as string so the input can be blank
}

interface PendingPayment {
  result: ApplyPaymentResult;
  confnum: string | null;
  finalizeError: string | null;
  finalizing: boolean;
  voiding: boolean;
}

export default function PaymentApplyPanel({ sid, anum, user }: { sid: number; anum: number; user?: User | null }) {
  const [cashCodes, setCashCodes] = useState<CashCode[]>([]);
  const [openInvoices, setOpenInvoices] = useState<StatementLine[]>([]);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  // Form state
  const [amount, setAmount] = useState('');
  const [cashCode, setCashCode] = useState('');
  const [reference, setReference] = useState('');
  const [tdate, setTdate] = useState(localYMD());
  const [rows, setRows] = useState<RowEntry[]>([]);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // After successful apply
  const [pending, setPending] = useState<PendingPayment | null>(null);

  // Prevent stale async updates after unmount
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  // Load cash codes + open invoices on mount or when sid/anum changes
  useEffect(() => {
    setLoadErr(null);
    let cancelled = false;

    Promise.all([
      fetchCashCodes(sid),
      fetchAccountStatement({ sid, anum, from: dateNDaysAgo(1825), to: localYMD() }),
    ])
      .then(([codes, stmt]) => {
        if (cancelled) return;
        setCashCodes(codes);
        const open = stmt.lines.filter((l) => l.due_mxn > 0);
        setOpenInvoices(open);
        setRows(open.map((l) => ({ line: l, applyAmt: '' })));
      })
      .catch((e: Error) => {
        if (!cancelled) setLoadErr(e.message ?? String(e));
      });

    return () => { cancelled = true; };
  }, [sid, anum]);

  // Derived tallies
  const amountNum = parseFloat(amount) || 0;
  const aplicado = useMemo(
    () => rows.reduce((s, r) => s + (parseFloat(r.applyAmt) || 0), 0),
    [rows],
  );
  const saldoAFavor = amountNum - aplicado;

  // Validation
  const rowOverApplied = rows.some((r) => {
    const a = parseFloat(r.applyAmt) || 0;
    return a > r.line.due_mxn + 0.0001; // float tolerance
  });
  // user is optional (fail open when the caller hasn't threaded one through)
  // — only actively blocks once we know a real User whose license blocks writes.
  const licenseOk = user == null || licenseAllowsWrites(user);
  const canSubmit = !submitting && amountNum > 0 && cashCode !== '' && aplicado <= amountNum + 0.0001 && !rowOverApplied && licenseOk;

  // FIFO auto-fill: distribute amountNum oldest-due-first (open invoices are
  // already ordered by tdate from the ledger; oldest date = lowest index).
  function autoFifo() {
    let remaining = amountNum;
    setRows((prev) =>
      prev.map((r) => {
        if (remaining <= 0) return { ...r, applyAmt: '' };
        const cap = r.line.due_mxn;
        const apply = Math.min(remaining, cap);
        remaining -= apply;
        // Round to 2 decimal places to avoid floating-point noise in the input
        return { ...r, applyAmt: apply > 0 ? apply.toFixed(2) : '' };
      }),
    );
  }

  function updateRow(idx: number, val: string) {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, applyAmt: val } : r)));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitErr(null);

    const linePayloads = rows
      .filter((r) => (parseFloat(r.applyAmt) || 0) > 0)
      .map((r) => ({ trankey: r.line.trankey, apply_amt: parseFloat(r.applyAmt) }));

    setSubmitting(true);
    try {
      const result = await applyPayment({
        sid,
        anum,
        amount: amountNum,
        cash_code: cashCode,
        reference,
        tdate,
        lines: linePayloads,
      });
      if (!alive.current) return;
      setPending({ result, confnum: null, finalizeError: null, finalizing: false, voiding: false });
    } catch (e) {
      if (alive.current) setSubmitErr((e as Error).message ?? t.payments.errors.generic);
    } finally {
      if (alive.current) setSubmitting(false);
    }
  }

  async function handleFinalize() {
    if (!pending) return;
    setPending((p) => p && ({ ...p, finalizing: true, finalizeError: null }));
    try {
      const res = await finalizePayment({ sid, tnum: pending.result.tkt_tnum });
      if (!alive.current) return;
      if (res.ok || res.already) {
        setPending((p) => p && ({ ...p, finalizing: false, confnum: res.confnum ?? null }));
      } else {
        setPending((p) => p && ({ ...p, finalizing: false, finalizeError: res.error ?? t.payments.errors.generic }));
      }
    } catch (e) {
      if (alive.current)
        setPending((p) => p && ({ ...p, finalizing: false, finalizeError: (e as Error).message ?? t.payments.errors.generic }));
    }
  }

  async function handleVoid() {
    if (!pending) return;
    setPending((p) => p && ({ ...p, voiding: true }));
    try {
      await voidPayment({ sid, payment_trankey: pending.result.payment_trankey });
      if (!alive.current) return;
      // Reset the whole panel
      setPending(null);
      setAmount('');
      setCashCode('');
      setReference('');
      setTdate(localYMD());
      setRows(openInvoices.map((l) => ({ line: l, applyAmt: '' })));
      setSubmitErr(null);
    } catch (e) {
      if (alive.current)
        setPending((p) => p && ({ ...p, voiding: false }));
    }
  }

  // ── Pending / post-apply view ───────────────────────────────────────────
  if (pending) {
    const { result, confnum, finalizeError, finalizing, voiding } = pending;
    return (
      <Card>
        <Title className="text-base">{t.payments.pendingTitle}</Title>
        <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-tremor-content text-xs">{t.payments.pendingApplied}</div>
            <div className="font-semibold tabular-nums">{fmtMxn.format(result.applied)}</div>
          </div>
          {result.on_account > 0 && (
            <div>
              <div className="text-tremor-content text-xs">{t.payments.pendingOnAccount}</div>
              <div className="tabular-nums">{fmtMxn.format(result.on_account)}</div>
            </div>
          )}
        </div>

        {confnum ? (
          <div className="mt-4 space-y-1">
            <Badge color="emerald">{t.payments.finalized}</Badge>
            <div className="text-xs text-tremor-content font-mono break-all">{confnum}</div>
          </div>
        ) : (
          <div className="mt-4 flex flex-wrap gap-3 items-center">
            <Button
              size="sm"
              onClick={handleFinalize}
              disabled={finalizing || voiding}
              loading={finalizing}
            >
              {finalizing ? t.payments.finalizing : t.payments.finalize}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              color="red"
              onClick={handleVoid}
              disabled={finalizing || voiding}
              loading={voiding}
            >
              {t.payments.undo}
            </Button>
            {finalizeError && (
              <Text className="text-red-600 dark:text-red-400 text-xs">{t.payments.finalizeError}: {finalizeError}</Text>
            )}
          </div>
        )}
      </Card>
    );
  }

  // ── Form view ───────────────────────────────────────────────────────────
  return (
    <Card>
      <Title className="text-base">{t.payments.title}</Title>

      {loadErr && (
        <Text className="text-red-600 dark:text-red-400 text-sm mt-2">{loadErr}</Text>
      )}

      <form onSubmit={handleSubmit} className="mt-4 space-y-4">
        {/* Top row: amount, cash code, reference, date */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">{t.payments.fields.amount}</div>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full rounded border border-tremor-border px-3 py-2"
              required
            />
          </label>

          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">{t.payments.fields.cashCode}</div>
            <Select
              value={cashCode}
              onValueChange={setCashCode}
              placeholder={t.payments.fields.cashCodePlaceholder}
              enableClear={false}
            >
              {cashCodes.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.code} · {c.desc}
                </SelectItem>
              ))}
            </Select>
          </label>

          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">{t.payments.fields.reference}</div>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className="w-full rounded border border-tremor-border px-3 py-2"
            />
          </label>

          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">{t.payments.fields.tdate}</div>
            <input
              type="date"
              value={tdate}
              onChange={(e) => setTdate(e.target.value)}
              className="w-full rounded border border-tremor-border px-3 py-2"
            />
          </label>
        </div>

        {/* Open invoice table */}
        {openInvoices.length === 0 && !loadErr && (
          <Text className="text-tremor-content text-sm italic">{t.payments.empty}</Text>
        )}

        {openInvoices.length > 0 && (
          <>
            <div className="flex items-center justify-between">
              <Text className="text-xs text-tremor-content">{t.payments.invoiceCols.apply}</Text>
              <Button
                type="button"
                size="xs"
                variant="secondary"
                onClick={autoFifo}
                disabled={amountNum <= 0}
              >
                {t.payments.autoFill}
              </Button>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>{t.payments.invoiceCols.inum}</TableHeaderCell>
                    <TableHeaderCell>{t.payments.invoiceCols.ddate}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.payments.invoiceCols.due}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.payments.invoiceCols.apply}</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, i) => {
                    const rowAmt = parseFloat(r.applyAmt) || 0;
                    const isOver = rowAmt > r.line.due_mxn + 0.0001;
                    return (
                      <TableRow key={`${r.line.sid}-${r.line.inum}-${r.line.trankey}`}>
                        <TableCell className="font-mono">{r.line.inum || '—'}</TableCell>
                        <TableCell>{r.line.ddate ?? '—'}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtMxn.format(r.line.due_mxn)}</TableCell>
                        <TableCell className="text-right">
                          <input
                            type="number"
                            min="0"
                            max={r.line.due_mxn}
                            step="0.01"
                            value={r.applyAmt}
                            onChange={(e) => updateRow(i, e.target.value)}
                            className={
                              'w-28 rounded border px-2 py-1 text-right tabular-nums ' +
                              (isOver ? 'border-red-500 text-red-600' : 'border-tremor-border')
                            }
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Tally */}
            <div className="flex gap-8 text-sm">
              <div>
                <span className="text-tremor-content text-xs">{t.payments.aplicado}: </span>
                <span className="font-semibold tabular-nums">{fmtMxn.format(aplicado)}</span>
              </div>
              <div>
                <span className="text-tremor-content text-xs">{t.payments.saldoAFavor}: </span>
                <span className={
                  'font-semibold tabular-nums ' +
                  (saldoAFavor < -0.001 ? 'text-red-600 dark:text-red-400' : 'text-tremor-content-strong')
                }>
                  {fmtMxn.format(saldoAFavor)}
                </span>
              </div>
            </div>
          </>
        )}

        {submitErr && (
          <Text className="text-red-600 dark:text-red-400 text-sm">{submitErr}</Text>
        )}

        <Button
          type="submit"
          disabled={!canSubmit}
          loading={submitting}
        >
          {t.payments.apply}
        </Button>
      </form>
    </Card>
  );
}
